import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { validateForm, resetPasswordValidationRules } from "../../utils/formValidation";
import { resetPassword } from "../../reduxStore/actions/userActionsHelper";
import { clearResetPasswordState } from "../../reduxStore/reducers/userReducersHelper";
import {
    userResetPasswordLoading$,
    userResetPasswordSuccess$,
    userResetPasswordError$
} from "../../reduxStore/selectors/userSelectors";
import styles from "./asset/resetPasswordForm.module.scss";

const ResetPasswordFormComponent = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Local state
    const [resetPasswordData, setResetPasswordData] = useState({
        newPassword: "",
        confirmPassword: ""
    });
    const [errors, setErrors] = useState({});
    const [isValidToken, setIsValidToken] = useState(false);
    const [token, setToken] = useState(null);

    // Redux state using selectors
    const isResetPasswordLoading = useSelector(userResetPasswordLoading$);
    const isResetPasswordSuccess = useSelector(userResetPasswordSuccess$);
    const resetPasswordError = useSelector(userResetPasswordError$);

    // Extract tokens from URL parameters on component mount (Supabase format)
    useEffect(() => {
        const parseSupabaseResponse = () => {
            console.log('Reset Password - Full URL:', window.location.href);

            // Parse both query parameters and hash parameters (Supabase can use either)
            const urlParams = new URLSearchParams(window.location.search);
            const hash = window.location.hash.substring(1);
            const hashParams = new URLSearchParams(hash);

            console.log('Query params:', Object.fromEntries(urlParams));
            console.log('Hash params:', Object.fromEntries(hashParams));

            // Try query params first, then hash params
            const urlToken = urlParams.get('token') || hashParams.get('access_token') || hashParams.get('token');
            const urlType = urlParams.get('type') || hashParams.get('type');

            console.log('Extracted Token:', urlToken);
            console.log('Extracted Type:', urlType);

            // Check if this is a password recovery request
            if (urlToken && urlType === 'recovery') {
                setToken(urlToken);
                setIsValidToken(true);
                console.log('Valid recovery token found');

                // Clear the URL parameters for security after a short delay
                setTimeout(() => {
                    window.history.replaceState(null, null, window.location.pathname);
                }, 1000);
            } else {
                console.log('Invalid or missing recovery token');
                setIsValidToken(false);
            }
        };

        parseSupabaseResponse();
    }, []);

    // Handle successful password reset
    useEffect(() => {
        if (isResetPasswordSuccess) {
            // Auto-navigate to sign-in after 3 seconds
            const timer = setTimeout(() => {
                dispatch(clearResetPasswordState());
                navigate('/sign-in');
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [isResetPasswordSuccess, dispatch, navigate]);

    const validateFormData = () => {
        const validation = validateForm(resetPasswordData, resetPasswordValidationRules);
        setErrors(validation.errors);
        return validation.isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateFormData()) {
            return;
        }

        if (!token) {
            setErrors({ general: "Invalid reset link. Please request a new password reset." });
            return;
        }

        const payload = {
            token: token,
            newPassword: resetPasswordData.newPassword
        };

        dispatch(resetPassword.request(payload));
    };

    const handleInputChange = (field, value) => {
        setResetPasswordData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear field error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const handleBackToSignIn = () => {
        dispatch(clearResetPasswordState());
        navigate('/sign-in');
    };

    // Show error for invalid token
    if (!isValidToken) {
        return (
            <div className={styles.resetPasswordForm}>
                <div className={styles.resetPasswordChild}>
                    <div className={styles.aceYourTestContainer}>
                        <p className={styles.aceYourTest}>Ace your test</p>
                        <p className={styles.aceYourTest}>with Prepal</p>
                    </div>
                    <img
                        className={styles.maskGroupIcon}
                        alt=""
                        src={require("../../publicAsset/media/book 1.svg").default}
                    />
                </div>
                <div className={styles.prepalLogo3Parent}>
                    <img
                        className={styles.prepalLogo3}
                        alt=""
                        src={require("../../publicAsset/media/Prepal Logo 3.svg").default}
                    />
                    <div className={styles.frameParent}>
                        <div className={styles.titleWrapper}>
                            <div className={styles.title}>Invalid Reset Link</div>
                        </div>
                        <div className={styles.errorContainer}>
                            <div className={styles.errorIcon}>⚠️</div>
                            <div className={styles.errorTitle}>Reset Link Invalid</div>
                            <div className={styles.errorMessage}>
                                This password reset link is invalid or has expired. Please request a new password reset.
                            </div>
                            <button
                                className={styles.backButton}
                                onClick={() => navigate('/forgot-password')}
                            >
                                <div className={styles.backButtonText}>Request New Reset</div>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Show success state
    if (isResetPasswordSuccess) {
        return (
            <div className={styles.resetPasswordForm}>
                <div className={styles.resetPasswordChild}>
                    <div className={styles.aceYourTestContainer}>
                        <p className={styles.aceYourTest}>Ace your test</p>
                        <p className={styles.aceYourTest}>with Prepal</p>
                    </div>
                    <img
                        className={styles.maskGroupIcon}
                        alt=""
                        src={require("../../publicAsset/media/book 1.svg").default}
                    />
                </div>
                <div className={styles.prepalLogo3Parent}>
                    <img
                        className={styles.prepalLogo3}
                        alt=""
                        src={require("../../publicAsset/media/Prepal Logo 3.svg").default}
                    />
                    <div className={styles.frameParent}>
                        <div className={styles.titleWrapper}>
                            <div className={styles.title}>Password Reset!</div>
                        </div>
                        <div className={styles.successContainer}>
                            <div className={styles.successIcon}>✅</div>
                            <div className={styles.successTitle}>Password Updated Successfully</div>
                            <div className={styles.successMessage}>
                                Your password has been reset successfully. You will be redirected to sign in shortly.
                            </div>
                            <button
                                className={styles.backButton}
                                onClick={handleBackToSignIn}
                            >
                                <div className={styles.backButtonText}>Sign In Now</div>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Show reset password form
    return (
        <div className={styles.resetPasswordForm}>
            <div className={styles.resetPasswordChild}>
                <div className={styles.aceYourTestContainer}>
                    <p className={styles.aceYourTest}>Ace your test</p>
                    <p className={styles.aceYourTest}>with Prepal</p>
                </div>
                <img
                    className={styles.maskGroupIcon}
                    alt=""
                    src={require("../../publicAsset/media/book 1.svg").default}
                />
            </div>
            <div className={styles.prepalLogo3Parent}>
                <img
                    className={styles.prepalLogo3}
                    alt=""
                    src={require("../../publicAsset/media/Prepal Logo 3.svg").default}
                />
                <div className={styles.frameParent}>
                    <div className={styles.titleWrapper}>
                        <div className={styles.title}>Reset Password</div>
                    </div>
                    <form className={styles.frameGroup} onSubmit={handleSubmit}>
                        <div className={styles.frameWrapper}>
                            <div className={styles.inputParent}>
                                {resetPasswordError && (
                                    <div className={styles.errorMessage}>
                                        {resetPasswordError}
                                    </div>
                                )}
                                {errors.general && (
                                    <div className={styles.errorMessage}>
                                        {errors.general}
                                    </div>
                                )}
                                <div className={styles.instructionText}>
                                    Enter your new password below. Make sure it's at least 6 characters long.
                                </div>

                                {/* New Password Field */}
                                <div className={styles.input}>
                                    <input
                                        className={styles.passwordInput}
                                        name="newPassword"
                                        type="password"
                                        placeholder="New Password"
                                        value={resetPasswordData.newPassword}
                                        required
                                        onChange={(e) => handleInputChange('newPassword', e.target.value)}
                                        disabled={isResetPasswordLoading}
                                    />
                                </div>
                                {errors.newPassword && (
                                    <div className={styles.fieldErrorMessage}>
                                        {errors.newPassword}
                                    </div>
                                )}

                                {/* Confirm Password Field */}
                                <div className={styles.input}>
                                    <input
                                        className={styles.passwordInput}
                                        name="confirmPassword"
                                        type="password"
                                        placeholder="Confirm New Password"
                                        value={resetPasswordData.confirmPassword}
                                        required
                                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                                        disabled={isResetPasswordLoading}
                                    />
                                </div>
                                {errors.confirmPassword && (
                                    <div className={styles.fieldErrorMessage}>
                                        {errors.confirmPassword}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={styles.buttonsParent}>
                            <button
                                type="submit"
                                className={styles.submitButton}
                                disabled={isResetPasswordLoading}
                            >
                                <div className={styles.submitButtonText}>
                                    {isResetPasswordLoading ? (
                                        <>
                                            <span className={styles.loadingSpinner}></span>
                                            Updating Password...
                                        </>
                                    ) : (
                                        "Update Password"
                                    )}
                                </div>
                            </button>
                        </div>
                    </form>

                    <div className={styles.backToSignInWrapper}>
                        <span
                            className={styles.backToSignIn}
                            onClick={handleBackToSignIn}
                        >
                            ← Back to Sign In
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordFormComponent;